import { create } from 'zustand';
import { Account, Asset, NetworkType, Policy } from '@mls-wallet/shared-types';
import { TransactionBuilderParameterState } from './parameter-inputs/types';
import type { RawContractTransaction } from './raw-transaction-import';
import type { RawTransaction } from './raw-transaction';
import type { ImportedTransactionChild } from './imported-transaction-draft';
import type {
  BatchTransactionChild,
  BatchTransactionInput,
} from './batch-transaction-draft';
import type { PolicyWithRateLimitInfo } from '@mls-wallet/shared-types';
import type {
  Contract,
  NativeTokenValue,
  SelectedContractFunction,
} from './types';

/**
 * Enum representing a transaction builder page
 * Used for navigating between pages
 */
export enum TransactionBuilderPageType {
  ACCOUNT_SELECTION_PAGE,
  NETWORK_SELECTION_PAGE,
  POLICY_SELECTION_PAGE,
  TRANSACTION_DETAILS_PAGE,
  DESCRIPTION_PAGE,
  REVIEW_PAGE,
  JSON_IMPORT_PAGE,
  CART_PAGE,
}

type TransactionBuilderPage = {
  page: TransactionBuilderPageType;
};

/**
 * Transaction type enum
 */
export enum TransactionTypeEnum {
  TOKEN_TRANSFER = 'token-transfer',
  CONTRACT_INTERACTION = 'contract-interaction',
}
export type TransactionType = `${TransactionTypeEnum}`;

/**
 * Token transfer details
 * Contains all the information needed for a token transfer transaction
 */
export interface TokenTransferDetails {
  displayAmount: string;
  recipient: {
    address: string;
    label: string;
  };
  asset: Asset;
}

/**
 * Draft token transfer values.
 * Unlike TokenTransferDetails, this can hold partially completed form state.
 */
interface TokenTransferDraft {
  displayAmount: string;
  recipient: TokenTransferDetails['recipient'] | null;
  asset: Asset | null;
}

export type ContractTransactionImportSource = 'walletconnect' | 'json-file';

/**
 * Contract interaction details
 * Contains all the information needed for a contract interaction transaction
 */
export interface ContractInteractionDetails extends Contract {
  selectedFunction?: SelectedContractFunction;
  functionParameters?: TransactionBuilderParameterState[];
  nativeTokenValue?: NativeTokenValue | undefined;
  /** Exact raw fields to submit when this draft came from custom JSON. */
  rawTransaction?: RawContractTransaction;
  /** Keeps the permitted import address exception across edits and navigation. */
  importSource?: ContractTransactionImportSource;
}

/**
 * State shape for the transaction builder store
 */
interface TransactionBuilderState {
  // Selected account for the transaction
  account: Account | undefined;
  network: NetworkType | undefined;
  // Selected policy for the transaction
  policy: Policy | undefined;
  // Current page in the transaction builder flow
  currentPage: TransactionBuilderPage;
  // Transaction type (token-transfer or contract-interaction)
  transactionType: TransactionType;
  // Final token transfer details (only set after the details step validates)
  tokenTransferDetails: TokenTransferDetails | undefined;
  // Draft token transfer details (updated as the user edits the details step)
  tokenTransferDraft: TokenTransferDraft | undefined;
  // Contract interaction details (only set when transactionType is 'contract-interaction')
  contractInteractionDetails: ContractInteractionDetails | undefined;
  // Transaction description
  description: string | undefined;
  importedTransactions: ImportedTransactionChild[] | undefined;
  importedFileName: string | undefined;
  batchTransactions: BatchTransactionChild[];
  editingTransactionId: string | undefined;
  editingRawTransaction: RawTransaction | undefined;
  /** A new session revokes async work owned by a previously opened child. */
  editorSessionId: string | undefined;
  draftRevision: number;

  // Actions
  setAccount: (account: Account | undefined) => void;
  setNetwork: (network: NetworkType | undefined) => void;
  setPolicy: (policy: Policy | undefined) => void;
  setTransactionType: (type: TransactionType) => void;
  setTokenTransferDraft: (draft: TokenTransferDraft) => void;
  setTokenTransferDetails: (details: TokenTransferDetails) => void;
  setContractInteractionDetails: (details: ContractInteractionDetails) => void;
  setFunctionParameters: (params: TransactionBuilderParameterState[]) => void;
  setNativeTokenValue: (value: NativeTokenValue | undefined) => void;
  clearRawTransaction: () => void;
  clearContractInteractionFunctionData: () => void;
  clearContractInteractionDetails: () => void;
  setDescription: (description: string) => void;
  setImportedTransactions: (
    transactions: RawTransaction[],
    fileName: string
  ) => void;
  clearImportedTransactions: () => void;
  setImportedTransactionPolicy: (
    childId: string,
    policy: PolicyWithRateLimitInfo | undefined
  ) => void;
  beginBatchTransaction: () => void;
  editBatchTransaction: (childId: string) => void;
  cancelBatchTransaction: () => void;
  saveBatchTransaction: (child: BatchTransactionInput) => void;
  appendImportedTransactions: () => void;
  removeBatchTransaction: (childId: string) => void;
  moveBatchTransaction: (childId: string, direction: -1 | 1) => void;
  navigateForwardToPage: (page: TransactionBuilderPage) => void;
  navigateBackward: () => void;
  reset: () => void;
}

/**
 * Initial state for the transaction builder
 */
const initialState = {
  account: undefined,
  network: undefined,
  policy: undefined,
  currentPage: { page: TransactionBuilderPageType.ACCOUNT_SELECTION_PAGE },
  transactionType: TransactionTypeEnum.TOKEN_TRANSFER,
  tokenTransferDetails: undefined,
  tokenTransferDraft: undefined,
  contractInteractionDetails: undefined,
  description: undefined,
  importedTransactions: undefined,
  importedFileName: undefined,
  batchTransactions: [],
  editingTransactionId: undefined,
  editingRawTransaction: undefined,
  editorSessionId: undefined,
  draftRevision: 0,
};

const emptyEditor = {
  policy: undefined,
  transactionType: TransactionTypeEnum.TOKEN_TRANSFER,
  tokenTransferDetails: undefined,
  tokenTransferDraft: undefined,
  contractInteractionDetails: undefined,
  editingTransactionId: undefined,
  editingRawTransaction: undefined,
  editorSessionId: undefined,
};

/**
 * Page order for navigation
 * Defines the sequence of pages in the transaction builder flow
 */
const PAGE_ORDER = [
  TransactionBuilderPageType.ACCOUNT_SELECTION_PAGE,
  TransactionBuilderPageType.NETWORK_SELECTION_PAGE,
  TransactionBuilderPageType.POLICY_SELECTION_PAGE,
  TransactionBuilderPageType.TRANSACTION_DETAILS_PAGE,
  TransactionBuilderPageType.DESCRIPTION_PAGE,
  TransactionBuilderPageType.REVIEW_PAGE,
];

/**
 * Zustand store for transaction builder state management
 *
 * Usage:
 * // Access full state
 * const store = useTransactionBuilderStore();
 *
 * // Or use selectors for optimal re-renders
 * const currentPage = useTransactionBuilderStore((state) => state.currentPage);
 * const navigateForward = useTransactionBuilderStore((state) => state.navigateForwardToPage);
 */
export const useTransactionBuilderStore = create<TransactionBuilderState>(
  (set, get) => ({
    ...initialState,

    /**
     * Sets the selected account for the transaction
     * Creates a new object to ensure React re-renders are triggered
     */
    setAccount: (account: Account | undefined) => {
      // Case: a different source account invalidates the cart and imported policy selections.
      if (get().account?.id !== account?.id) {
        set({
          ...emptyEditor,
          batchTransactions: [],
          description: undefined,
          draftRevision: get().draftRevision + 1,
        });
        get().clearImportedTransactions();
      }
      set({ account: account ? { ...account } : undefined });
    },

    /**
     * Sets the selected network to use on the account
     */
    setNetwork: (network: NetworkType | undefined) => {
      // Case: cart payloads and selected policies must not carry over to another network.
      if (get().network !== network) {
        set({
          ...emptyEditor,
          batchTransactions: [],
          description: undefined,
          draftRevision: get().draftRevision + 1,
        });
        get().clearImportedTransactions();
      }
      set({ network });
    },

    /**
     * Sets the selected policy for the transaction
     */
    setPolicy: (policy: Policy | undefined) => {
      // Clear contract interaction details when policy changes
      // (new policy may have different contract/function restrictions)
      set({
        policy: policy ? { ...policy } : undefined,
        importedTransactions: undefined,
        importedFileName: undefined,
        tokenTransferDetails: undefined,
        tokenTransferDraft: undefined,
        contractInteractionDetails: undefined,
      });
    },

    /**
     * Sets the transaction type (token-transfer or contract-interaction)
     */
    setTransactionType: (type: TransactionType) => {
      set({ transactionType: type });
    },

    /**
     * Sets the token transfer draft while the user is editing the details page.
     */
    setTokenTransferDraft: (draft: TokenTransferDraft) => {
      set({
        tokenTransferDraft: {
          displayAmount: draft.displayAmount,
          recipient: draft.recipient ? { ...draft.recipient } : null,
          asset: draft.asset ? { ...draft.asset } : null,
        },
      });
    },

    /**
     * Sets the token transfer details (token address, amount, and recipient)
     * This should only be called when transactionType is 'token-transfer'
     */
    setTokenTransferDetails: (details: TokenTransferDetails) => {
      set({
        tokenTransferDetails: {
          ...details,
          recipient: { ...details.recipient },
          asset: { ...details.asset },
        },
        tokenTransferDraft: {
          displayAmount: details.displayAmount,
          recipient: { ...details.recipient },
          asset: { ...details.asset },
        },
      });
    },

    /**
     * Sets the contract interaction details (contract address and name)
     * This should only be called when transactionType is 'contract-interaction'
     */
    setContractInteractionDetails: (details: ContractInteractionDetails) => {
      set({
        contractInteractionDetails: {
          ...details,
          ...(details.rawTransaction
            ? { rawTransaction: { ...details.rawTransaction } }
            : {}),
        },
      });
    },

    /**
     * Sets the function parameters for contract interaction
     */
    setFunctionParameters: (params: TransactionBuilderParameterState[]) => {
      const existing = get().contractInteractionDetails;
      if (!existing) return;
      set({
        contractInteractionDetails: {
          ...existing,
          functionParameters: params.map((p) => ({ ...p })),
        },
      });
    },

    /**
     * Sets the native token value for payable contract functions
     */
    setNativeTokenValue: (value: NativeTokenValue | undefined) => {
      const existing = get().contractInteractionDetails;
      if (!existing) return;
      set({
        contractInteractionDetails: {
          ...existing,
          nativeTokenValue: value ? { ...value } : undefined,
        },
      });
    },

    /**
     * Stops using imported bytes after the user edits a decoded form value.
     */
    clearRawTransaction: () => {
      const existing = get().contractInteractionDetails;
      if (!existing?.rawTransaction) return;

      const nextDetails: ContractInteractionDetails = { ...existing };
      delete nextDetails.rawTransaction;
      set({ contractInteractionDetails: nextDetails });
    },

    /**
     * Clears function-specific data while preserving the selected contract.
     * Useful when switching between functions to prevent stale data.
     */
    clearContractInteractionFunctionData: () => {
      const existing = get().contractInteractionDetails;
      if (!existing) return;
      set({
        contractInteractionDetails: {
          address: existing.address,
          contractName: existing.contractName,
          implementationFunctions: existing.implementationFunctions,
          proxyFunctions: existing.proxyFunctions,
          allowedProxyFunctions: existing.allowedProxyFunctions,
          allowedImplementationFunctions:
            existing.allowedImplementationFunctions,
          functionParameters: [],
        },
      });
    },

    /**
     * Clears all contract interaction details.
     * Used when user clears the contract selection.
     */
    clearContractInteractionDetails: () => {
      set({ contractInteractionDetails: undefined });
    },

    /**
     * Sets the transaction description
     */
    setDescription: (description: string) => {
      set({ description });
    },

    setImportedTransactions: (transactions, fileName) => {
      // Install the complete validated file at once; never mix it with a
      // previous import or the single-transaction form's policy and payload.
      set({
        importedTransactions: transactions.map((transaction) => ({
          ...transaction,
          id: crypto.randomUUID(),
          policy: undefined,
        })),
        importedFileName: fileName,
        policy: undefined,
        tokenTransferDetails: undefined,
        tokenTransferDraft: undefined,
        contractInteractionDetails: undefined,
        description: get().batchTransactions.length
          ? get().description
          : undefined,
      });
    },

    clearImportedTransactions: () => {
      set({ importedTransactions: undefined, importedFileName: undefined });
    },

    setImportedTransactionPolicy: (childId, policy) => {
      set((state) => ({
        importedTransactions: state.importedTransactions?.map((child) =>
          child.id === childId ? { ...child, policy } : child
        ),
      }));
    },

    beginBatchTransaction: () => {
      set({
        ...emptyEditor,
        editorSessionId: crypto.randomUUID(),
        currentPage: { page: TransactionBuilderPageType.POLICY_SELECTION_PAGE },
      });
    },

    editBatchTransaction: (childId) => {
      const child = get().batchTransactions.find(({ id }) => id === childId);
      if (!child) return;
      // The form owns an isolated copy. Cancel must leave the saved cart row
      // unchanged even when nested ABI parameters are edited in place.
      const copy = structuredClone(child);
      const editor = copy.editor;
      set({
        ...emptyEditor,
        policy: copy.policy,
        editingTransactionId: childId,
        editorSessionId: crypto.randomUUID(),
        transactionType:
          editor?.transactionType ?? TransactionTypeEnum.CONTRACT_INTERACTION,
        tokenTransferDetails:
          editor?.transactionType === 'token-transfer'
            ? editor.tokenTransferDetails
            : undefined,
        contractInteractionDetails:
          editor?.transactionType === 'contract-interaction'
            ? editor.contractInteractionDetails
            : undefined,
        editingRawTransaction: editor
          ? undefined
          : {
              to: copy.payload.to,
              value: copy.payload.value,
              data: copy.payload.data,
            },
        currentPage: {
          page: TransactionBuilderPageType.TRANSACTION_DETAILS_PAGE,
        },
      });
    },

    cancelBatchTransaction: () => {
      set({
        ...emptyEditor,
        currentPage: { page: TransactionBuilderPageType.CART_PAGE },
      });
    },

    saveBatchTransaction: (child) => {
      const { editingTransactionId, batchTransactions, editorSessionId } =
        get();
      if (!editorSessionId) return;
      if (
        editingTransactionId &&
        !batchTransactions.some(({ id }) => id === editingTransactionId)
      )
        return;
      const saved = structuredClone({
        ...child,
        id: editingTransactionId ?? crypto.randomUUID(),
      });
      set({
        ...emptyEditor,
        batchTransactions: editingTransactionId
          ? batchTransactions.map((existing) =>
              existing.id === editingTransactionId ? saved : existing
            )
          : [...batchTransactions, saved],
        draftRevision: get().draftRevision + 1,
        currentPage: { page: TransactionBuilderPageType.CART_PAGE },
      });
    },

    appendImportedTransactions: () => {
      const imported = get().importedTransactions;
      if (!imported?.length || imported.some(({ policy }) => !policy)) return;
      const children: BatchTransactionChild[] = imported.flatMap((child) =>
        child.policy
          ? [
              {
                id: child.id,
                policy: child.policy,
                payload: {
                  to: child.to,
                  value: child.value,
                  data: child.data,
                  policyId: child.policy.id,
                },
              },
            ]
          : []
      );
      set({
        ...emptyEditor,
        batchTransactions: [
          ...get().batchTransactions,
          ...structuredClone(children),
        ],
        importedTransactions: undefined,
        importedFileName: undefined,
        draftRevision: get().draftRevision + 1,
        currentPage: { page: TransactionBuilderPageType.CART_PAGE },
      });
    },

    removeBatchTransaction: (childId) => {
      set((state) => ({
        batchTransactions: state.batchTransactions.filter(
          ({ id }) => id !== childId
        ),
        draftRevision: state.draftRevision + 1,
      }));
    },

    moveBatchTransaction: (childId, direction) => {
      const children = [...get().batchTransactions];
      const from = children.findIndex(({ id }) => id === childId);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= children.length) return;
      const [child] = children.splice(from, 1);
      if (!child) return;
      children.splice(to, 0, child);
      set({
        batchTransactions: children,
        draftRevision: get().draftRevision + 1,
      });
    },

    /**
     * Navigates to a specific page in the transaction builder
     */
    navigateForwardToPage: (page: TransactionBuilderPage) => {
      set({ currentPage: page });
    },

    /**
     * Navigates back to the previous page in the transaction builder
     * Uses the PAGE_ORDER array to determine the previous page
     * Clears contract/token details when going back to policy selection
     */
    navigateBackward: () => {
      const currentPageType = get().currentPage.page;
      // Case: leaving JSON import discards the pending import and returns to the cart.
      if (currentPageType === TransactionBuilderPageType.JSON_IMPORT_PAGE) {
        get().clearImportedTransactions();
        set({
          currentPage: {
            page: TransactionBuilderPageType.CART_PAGE,
          },
        });
        return;
      }
      if (currentPageType === TransactionBuilderPageType.CART_PAGE) {
        set({
          currentPage: {
            page: TransactionBuilderPageType.NETWORK_SELECTION_PAGE,
          },
        });
        return;
      }
      // Case: returning from description preserves the existing children in the cart.
      if (
        currentPageType === TransactionBuilderPageType.DESCRIPTION_PAGE &&
        get().batchTransactions.length > 0
      ) {
        set({
          currentPage: { page: TransactionBuilderPageType.CART_PAGE },
        });
        return;
      }
      const currentPageIndex = PAGE_ORDER.indexOf(currentPageType);

      if (currentPageIndex > 0) {
        const previousPage = PAGE_ORDER[currentPageIndex - 1];
        if (previousPage === undefined) {
          throw new Error('Previous page not found in PAGE_ORDER');
        }

        // Clear transaction details when going back to policy selection
        // (user might select a different policy)
        if (previousPage === TransactionBuilderPageType.POLICY_SELECTION_PAGE) {
          get().clearContractInteractionDetails();
          set({
            tokenTransferDetails: undefined,
            tokenTransferDraft: undefined,
          });
        }

        set({
          currentPage: { page: previousPage },
        });
      }
    },

    /**
     * Resets the store to its initial state
     * Should be called when the transaction builder is unmounted
     */
    reset: () => {
      set(initialState);
    },
  })
);
